import type { GridSortModel } from "@mui/x-data-grid-premium";
import type { AnyRecord, StateAction } from "@entrilia/shared/types";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { ObjectFieldSort, ObjectFilter } from "../../../api/types/objectTypes";
import type { PersistedFilterState } from "../../../storage/storage";
import storage from "../../../storage/storage";
import type { TableColumnDefinition } from "../columns/columnDefinitions";
import type {
  FilterValue,
  GridSortItem,
  SearchFilter,
  SearchFilterDefinition,
  TableFilter,
  TableFilterDefinition,
} from "./filterTypes";
import {
  emptySearchFilterDefinition,
  filterTableRows,
  getDefaultFilterValue,
  getFilterDefinition,
  initializeFilter,
  initializeSearchFilter,
} from "./handlers/filterHandlers";
import { mapTableFilterToApiObjectFilter } from "./objectFilterMapper";

export interface FilterState<R extends AnyRecord> {
  filterId: string;
  showFilters: boolean;
  visibleFilters: TableFilter<R>[];
  availableFilters: TableFilterDefinition<R>[];
  search: SearchFilter<R>;
  visibleColumns: TableColumnDefinition[];
  availableColumns: TableColumnDefinition[];
  sorting: GridSortItem | undefined;
}

const initializeFilters = <R extends AnyRecord>(
  filterDefinitions: TableFilterDefinition<R>[],
  persistedState: PersistedFilterState | undefined,
  predefinedFilters: TableFilter<R>[]
) => {
  if (persistedState) {
    const visibleFilters = predefinedFilters;
    const visibleFilterIds = new Set(predefinedFilters.map((f) => f.id));

    for (const storedFilter of persistedState.filters) {
      const definition = filterDefinitions.find((def) => def.id === storedFilter.id && def.type === storedFilter.type);
      if (definition && !visibleFilterIds.has(definition.id)) {
        const filter = initializeFilter(definition);
        filter.value = storedFilter.value;
        visibleFilters.push(filter);
        visibleFilterIds.add(definition.id);
      }
    }

    const availableFilters = filterDefinitions.filter((def) => !visibleFilterIds.has(def.id));

    return {
      visibleFilters,
      availableFilters,
    };
  }

  const predefinedFilterIds = new Set(predefinedFilters.map((f) => f.id));
  return {
    visibleFilters: [...predefinedFilters],
    availableFilters: [...filterDefinitions.filter((def) => !predefinedFilterIds.has(def.id))],
  };
};

const initializeColumns = (
  columnDefinitions: TableColumnDefinition[],
  persistedState: PersistedFilterState | undefined
) => {
  const visibleColumns: TableColumnDefinition[] = [];
  const visibleColumnIds = new Set<string>();

  if (persistedState) {
    for (const storedColumn of persistedState.columns) {
      const definition = columnDefinitions.find((def) => def.id === storedColumn.id);
      if (definition) {
        visibleColumns.push(definition);
        visibleColumnIds.add(definition.id);
      }
    }
  } else {
    for (const def of columnDefinitions) {
      if (!def.defaultHidden) {
        visibleColumns.push({ ...def });
        visibleColumnIds.add(def.id);
      }
    }

    if (visibleColumns.length === 0) {
      const firstColumn = columnDefinitions[0];
      if (firstColumn) {
        visibleColumns.push({ ...firstColumn });
        visibleColumnIds.add(firstColumn.id);
      }
    }
  }

  const availableColumns = columnDefinitions.filter((def) => !visibleColumnIds.has(def.id));

  return {
    visibleColumns,
    availableColumns,
  };
};

export const getInitialFilterState = <R extends AnyRecord>(
  filterId: string,
  filterDefinitions: TableFilterDefinition<R>[],
  predefinedFilters: TableFilter<R>[],
  columnDefinitions?: TableColumnDefinition[],
  searchDefinition?: SearchFilterDefinition<R>
): FilterState<R> => {
  const persistedState = storage.getFilterState(filterId);

  const { visibleFilters, availableFilters } = initializeFilters(filterDefinitions, persistedState, predefinedFilters);

  const { visibleColumns, availableColumns } = initializeColumns(columnDefinitions ?? [], persistedState);

  const search = initializeSearchFilter(
    searchDefinition ?? emptySearchFilterDefinition(),
    persistedState?.searchValue || ""
  );

  const sorting = persistedState?.sorting ?? undefined;

  return {
    filterId,
    showFilters: false,
    visibleFilters,
    availableFilters,
    search,
    visibleColumns,
    availableColumns,
    sorting,
  };
};

const saveFilterState = <R extends AnyRecord>(state: FilterState<R>) => {
  const filters = state.visibleFilters.map(({ id, type, value }) => ({
    id,
    type,
    value,
  }));

  const columns = state.visibleColumns.map(({ id }) => ({ id }));
  const searchValue = state.search.value.trim() || undefined;
  const sorting = state.sorting?.sort ? { field: state.sorting.field, sort: state.sorting.sort } : undefined;

  storage.saveFilterState(state.filterId, { filters, columns, searchValue, sorting });
};

export const getAppliedMultiSelectFilterValues = <R extends AnyRecord>(filterId: string, state: FilterState<R>) => {
  const filter = state.visibleFilters.find(({ id }) => id === filterId);
  if (filter?.type === "multi_select" && filter.value.operator === "one_of") {
    return filter.value.selectedValues ?? [];
  }
  return [];
};

export const getAppliedMultiSelectFilterLabels = <R extends AnyRecord>(filterId: string, state: FilterState<R>) => {
  const filter = state.visibleFilters.find(({ id }) => id === filterId);
  if (filter?.type === "multi_select" && filter.value.operator === "one_of") {
    return filter.value.selectedLabels ?? [];
  }
  return [];
};

export const filterRecords = <R extends AnyRecord>(state: FilterState<R>, rows: R[]): R[] =>
  filterTableRows(rows, state.visibleFilters, state.search);

export const getVisibleColumnsAndFiltersIds = <R extends AnyRecord>(state: FilterState<R>): string[] => {
  const visibleColumnsFieldIds = state.visibleColumns.filter((col) => col.dynamic).map(({ id }) => id);
  const visibleFiltersFieldIds = state.visibleFilters.filter((filter) => filter.dynamic).map(({ id }) => id);
  return distinct([...visibleColumnsFieldIds, ...visibleFiltersFieldIds]);
};

export const getApiObjectFilters = <R extends AnyRecord>(
  state: FilterState<R>
): { filters: ObjectFilter[]; searchTerm: string | undefined; sort: ObjectFieldSort | undefined } => ({
  filters: state.visibleFilters
    .map((filter) => mapTableFilterToApiObjectFilter(filter))
    .filter(Boolean) as ObjectFilter[],
  searchTerm: state.search.value || undefined,
  sort: state.sorting ? { fieldId: state.sorting.field, ascending: state.sorting.sort === "asc" } : undefined,
});

// Actions

export const toggleVisibilityAction =
  <R extends AnyRecord>(): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => ({
    ...state,
    showFilters: !state.showFilters,
  });

export const resetFiltersAction =
  <R extends AnyRecord>(): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    state.visibleFilters.forEach((filter) => {
      if (!filter.readOnly) {
        filter.value = getDefaultFilterValue(filter);
      }
    });
    saveFilterState(state);
    return { ...state };
  };

export const updateFilterValueAction =
  <R extends AnyRecord>(filterId: string, newValue: FilterValue): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const filter = state.visibleFilters.find((f) => f.id === filterId);
    if (filter) {
      filter.value = newValue;
    }
    saveFilterState(state);
    return { ...state };
  };

export const searchAction =
  <R extends AnyRecord>(searchTerm: string): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const newState = { ...state, search: { ...state.search, value: searchTerm.trim() } };
    saveFilterState(newState);
    return newState;
  };

export const addFilterAction =
  <R extends AnyRecord>(filterId: string): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const filterToAdd = state.availableFilters.find((f) => f.id === filterId);
    if (!filterToAdd) {
      return { ...state };
    }

    const newState = {
      ...state,
      visibleFilters: [...state.visibleFilters, initializeFilter(filterToAdd)],
      availableFilters: state.availableFilters.filter((f) => f.id !== filterId),
    };

    saveFilterState(newState);
    return newState;
  };

export const removeFilterAction =
  <R extends AnyRecord>(filterId: string): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const filterToRemove = state.visibleFilters.find((f) => f.id === filterId);
    if (!filterToRemove) {
      return { ...state };
    }

    const newState = {
      ...state,
      visibleFilters: state.visibleFilters.filter((f) => f.id !== filterId),
      availableFilters: [...state.availableFilters, getFilterDefinition(filterToRemove)],
    };

    saveFilterState(newState);
    return newState;
  };

export const updateColumnsAction =
  <R extends AnyRecord>(
    visibleColumns: TableColumnDefinition[],
    availableColumns: TableColumnDefinition[]
  ): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const newState = {
      ...state,
      visibleColumns,
      availableColumns,
    };
    saveFilterState(newState);
    return newState;
  };

export const sortAction =
  <R extends AnyRecord>(gridSortModel: GridSortModel): StateAction<FilterState<R>> =>
  (state: FilterState<R>) => {
    const firstSortItem = gridSortModel[0];
    const sorting = firstSortItem?.sort ? firstSortItem : undefined;
    const newState = { ...state, sorting };
    saveFilterState(newState);
    return newState;
  };
